import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Info, X } from 'lucide-react';

import { Composer } from '@/components/app-shell/support/Composer';
import { MessageList } from '@/components/app-shell/support/MessageList';
import {
  nativeFeedMissing,
  useBootstrap,
  useMarkSupportRead,
  useSendSupportMessage,
  useSupportRequests,
  useNativePatientRequests,
  useSupportThread,
} from '@/lib/api/hooks';
import {
  SUPPORT_ATTACHMENT_LIMIT,
  SUPPORT_ATTACHMENT_TYPES,
  supportAttachmentAllowed,
  supportAttachmentsRefused,
  useReadSupportAttachment,
  useReleaseSupportAttachment,
  useUploadSupportAttachment,
  type SupportAttachment,
} from '@/lib/api/supportAttachments';
import { useAuth } from '@/lib/auth/AuthProvider';
import { ApiError } from '@/lib/api/client';
import type { SupportMessage, SupportRequest } from '@/types/api';

import { messagingHub, useMessagingHub } from '../messagingHub';
import { eventCopy, openRequests } from '../requestStatus';
import { nativeSupportEnabled } from '@/features/native-support/types';
import { nativeEventLine, nativeOpen } from '@/features/native-support/requestLabels';
import { compareTimeline } from '@/features/native-support/requestFeed';
import { ArrowInline } from '@/lib/Icon';
import type { Request } from './Requests';
import { RequestDetail, RequestStrip, RequestUpdateCard, RequestsList } from './Requests';
import { SupportAvatar, type Provider } from './TeamAvatars';

const EVENT_PREFIX = 'request-event:';

type PickedImage = { key: string; file: File; attachment?: SupportAttachment; failed?: boolean };
type PickedImages = { owner: string; items: PickedImage[]; refused: boolean };

/**
 * System Support inside the Messages drawer: one continuous AI-first chat plus
 * request status (tickets shown read-only). The patient never picks a request,
 * ends or rates the chat, and the pane makes no reply-time promise of its own
 * (WF07-R2, WF07-R11, WF07-R14).
 */
export function SupportPane(_props: { providers: Provider[] }) {
  const hub = useMessagingHub();
  const { session } = useAuth();
  const { data: thread } = useSupportThread();
  const { data: bootstrap } = useBootstrap();
  const native = nativeSupportEnabled();
  // Keep one ephemeral draft while the pane switches to request history.
  // Tag it to the current patient and thread, and in the native build the
  // signed-in user, so identity changes never render another conversation's
  // text, even before an effect could run.
  const draftOwner = `${native ? session?.user.id ?? '' : ''}:${bootstrap?.patient.id ?? ''}:${thread?.thread_id ?? ''}`;
  const [draft, setDraft] = useState({ owner: draftOwner, value: '' });
  const draftValue = draft.owner === draftOwner ? draft.value : '';
  const [images, setImages] = useState<PickedImages>({ owner: draftOwner, items: [], refused: false });
  const sentImages = useRef<PickedImage[]>([]);
  const removedImages = useRef(new Set<string>());
  const pendingUploads = useRef(new Map<string, Promise<SupportAttachment | undefined>>());
  const pendingReleases = useRef(new Set<Promise<void>>());
  const activeOwner = useRef<string | null>(draftOwner);
  const releaseController = useRef<AbortController | null>(null);
  activeOwner.current = draftOwner;
  useLayoutEffect(() => {
    activeOwner.current = draftOwner;
    const controller = new AbortController();
    releaseController.current = controller;
    return () => {
      activeOwner.current = null;
      controller.abort();
    };
  }, [draftOwner]);
  const ownImages = images.owner === draftOwner ? images : { owner: draftOwner, items: [], refused: false };
  useEffect(() => {
    setDraft(previous => previous.owner === draftOwner ? previous : { owner: draftOwner, value: '' });
    setImages(previous => previous.owner === draftOwner ? previous : { owner: draftOwner, items: [], refused: false });
    sentImages.current = [];
    removedImages.current.clear();
    pendingUploads.current.clear();
    pendingReleases.current.clear();
  }, [draftOwner]);

  const { data: legacyRequestsData } = useSupportRequests(!native);
  const nativeFeed = useNativePatientRequests(native);
  const sendMessage = useSendSupportMessage();
  const markRead = useMarkSupportRead();
  const upload = useUploadSupportAttachment();
  const release = useReleaseSupportAttachment();
  const nextImage = useRef(0);
  const sentOwner = useRef(draftOwner);
  const failed = native && sendMessage.isError ? sendMessage.variables : undefined;
  const failedText = typeof failed === 'string' ? failed : failed?.body;
  const refusedIds = failedText !== undefined && supportAttachmentsRefused(sendMessage.error);
  useEffect(() => {
    if (failedText === undefined) return;
    if (sentOwner.current === draftOwner) {
      const restored = refusedIds ? sentImages.current.map(({ key, file }) => ({ key, file, failed: true })) : sentImages.current;
      setDraft(previous => previous.owner === draftOwner && !previous.value ? { owner: draftOwner, value: failedText } : previous);
      setImages(previous => previous.owner === draftOwner && !previous.items.length ? { ...previous, items: restored } : previous);
    }
    sendMessage.reset();
  }, [failedText, refusedIds, draftOwner, sendMessage.reset]);

  const patchImage = (owner: string, key: string, change: Partial<PickedImage>) => setImages(previous => previous.owner !== owner ? previous : {
    ...previous, items: previous.items.map(item => item.key === key ? { ...item, ...change } : item),
  });
  const releaseImage = async (owner: string, attachment: SupportAttachment, controller: AbortController) => {
    // Release is idempotent, but an uncertain result is never a reason to
    // retry under another signed-in account or after this draft unmounts.
    for (let attempt = 0; attempt < 2 && activeOwner.current === owner && !controller.signal.aborted; attempt += 1) {
      try {
        await release(attachment.id, controller.signal);
        return;
      } catch (error) {
        if (!(error instanceof ApiError ? error.status === 503 : error instanceof TypeError)) return;
      }
    }
    // No quota-success claim: the existing expiry/sweep remains the fallback
    // when both attempts have an unknown outcome.
  };
  const uploadImage = (owner: string, item: PickedImage) => {
    const controller = releaseController.current;
    if (!controller) return;
    const current = () => activeOwner.current === owner && !controller.signal.aborted;
    patchImage(owner, item.key, { failed: false });
    // Snapshot only earlier removals. A replacement removed while waiting can
    // join the queue without ever making an upload depend on its own release.
    const earlierReleases = [...pendingReleases.current];
    const operation = (async () => {
      await Promise.all(earlierReleases);
      if (!current() || removedImages.current.has(item.key)) return;
      return upload(item.file, controller.signal);
    })();
    pendingUploads.current.set(item.key, operation);
    operation.then(
      attachment => {
        if (attachment && current() && !removedImages.current.has(item.key)) patchImage(owner, item.key, { attachment });
      },
      () => { if (current()) patchImage(owner, item.key, { failed: true }); },
    );
  };
  const removeImage = (item: PickedImage) => {
    removedImages.current.add(item.key);
    setImages(previous => previous.owner === draftOwner ? {
      ...previous, items: previous.items.filter(entry => entry.key !== item.key),
    } : previous);
    const controller = releaseController.current;
    const uploaded = item.attachment ? Promise.resolve(item.attachment) : pendingUploads.current.get(item.key);
    pendingUploads.current.delete(item.key);
    if (!controller || !uploaded) return;
    const removal = uploaded.then(async attachment => {
      if (attachment) await releaseImage(draftOwner, attachment, controller);
    }, () => undefined);
    pendingReleases.current.add(removal);
    void removal.then(() => pendingReleases.current.delete(removal));
  };
  const pickImages = (files: File[]) => {
    if (!bootstrap?.patient.id || !thread?.thread_id) return;
    if (ownImages.items.length + files.length > SUPPORT_ATTACHMENT_LIMIT || !files.every(supportAttachmentAllowed)) {
      setImages(previous => previous.owner === draftOwner ? { ...previous, refused: true } : { owner: draftOwner, items: [], refused: true });
      return;
    }
    const added = files.map(file => ({ key: String(nextImage.current++), file }));
    setImages(previous => ({ owner: draftOwner, items: [...(previous.owner === draftOwner ? previous.items : []), ...added], refused: false }));
    for (const item of added) uploadImage(draftOwner, item);
  };
  // The patient's own images sit on their side and a rep's on the rep's side
  // (WF07-R16). Sonny's and system messages carry none.
  const renderImages = (m: SupportMessage) => {
    const rep = m.sender_role === 'human_agent';
    if (!(rep || m.sender_role === 'patient') || !m.attachments?.length) return null;
    return (
      <div className={`ph-no-capture flex flex-col ${rep ? 'items-start' : 'items-end'} gap-6 px-15`}>
        {m.attachments.map(attachment => (
          <AttachmentImage key={attachment.id} attachment={attachment} owner={draftOwner}
            alt={rep ? 'Image from our team' : 'Evidence attached to this message'} />
        ))}
      </div>
    );
  };

  const view = hub.supportView;
  const inChat = view.kind === 'chat';

  useEffect(() => {
    if (inChat && thread?.unread_count) markRead.mutate();
  }, [inChat, thread?.unread_count, markRead.mutate]);

  const requests: Request[] = useMemo(() => native ? nativeFeed.data?.requests ?? [] : legacyRequestsData?.requests ?? [], [native, nativeFeed.data, legacyRequestsData]);
  const open = useMemo(() => native ? requests.filter(r => 'label_key' in r && nativeOpen(r)) : openRequests(requests.filter((r): r is SupportRequest => !('label_key' in r))), [native, requests]);

  const messages = thread?.messages ?? [];
  // Older reply receipts and recent status changes can refer to requests
  // beyond the first request page. Hydrate only the existing identity-scoped
  // feed, stopping as soon as those IDs are found or requests are exhausted.
  const missingTimelineRequest = native && (
    messages.some(message => message.sender_role === 'human_agent' && message.ticket_id
      && !requests.some(request => request.id === message.ticket_id))
    || (nativeFeed.data?.events ?? []).some(event => !requests.some(request => request.id === event.request_id))
  );
  useEffect(() => {
    if (inChat && missingTimelineRequest && nativeFeed.data
      && !nativeFeed.data.requests_complete && nativeFeed.hasNextPage
      && !nativeFeed.isFetchingNextPage && !nativeFeed.isError) {
      void nativeFeed.fetchNextPage();
    }
  }, [inChat, missingTimelineRequest, nativeFeed.data, nativeFeed.hasNextPage,
    nativeFeed.isFetchingNextPage, nativeFeed.isError, nativeFeed.fetchNextPage]);

  // Request cards in the chat timeline, placed at their event's time. Native:
  // a card only when a request opens (or reopens) and when it is resolved;
  // other status changes show only in the request's own status history.
  // Legacy: each request's latest event is one card, so a status change reads
  // as an update rather than a new request. Rendered client-side from the
  // requests feed; nothing is written into the support thread.
  const timeline = useMemo(() => {
    const byId = new Map(requests.map((r) => [r.id, r]));
    const nativeEvents = new Map((nativeFeed.data?.events ?? []).map(event => [event.id, event]));
    const synthetic: SupportMessage[] = native ? (nativeFeed.data?.events ?? []).filter(event => event.kind === 'opened' || event.kind === 'resolved').map(event => ({
      id: `${EVENT_PREFIX}${event.id}`, sender_role: 'system', sender_name: null,
      body: '', created_at: event.at,
    })) : requests.flatMap((r) => {
      if ('label_key' in r) return [];
      const latest = r.events.length - 1;
      if (latest < 0) return [];
      return [
        {
          id: `${EVENT_PREFIX}${r.id}:${latest}`,
          sender_role: 'system' as const,
          sender_name: null,
          body: '',
          created_at: r.events[latest].at,
        },
      ];
    });
    const merged = [...messages, ...synthetic].sort((a, b) => compareTimeline(
      { id: a.id, at: a.created_at, event: a.id.startsWith(EVENT_PREFIX) },
      { id: b.id, at: b.created_at, event: b.id.startsWith(EVENT_PREFIX) },
    ));
    return { merged, byId, nativeEvents };
  }, [messages, requests, native, nativeFeed.data]);

  const renderMessage = (m: SupportMessage) => {
    if (!m.id.startsWith(EVENT_PREFIX)) return null;
    if (native) {
      const event = timeline.nativeEvents.get(m.id.slice(EVENT_PREFIX.length));
      const request = event && timeline.byId.get(event.request_id);
      const title = event && nativeEventLine(event.kind);
      if (!request || !event || !title) return <></>;
      return <RequestUpdateCard compact requestTitle={request.title} at={event.at} title={title}
        onViewStatus={() => messagingHub.showSupportView({ kind: 'request', id: request.id })} />;
    }
    const [requestId, index] = m.id.slice(EVENT_PREFIX.length).split(':');
    const r = timeline.byId.get(requestId);
    const e = r && !('label_key' in r) ? r.events[Number(index)] : undefined;
    const copy = r && !('label_key' in r) && e ? eventCopy(r, e) : null;
    if (!r || !e || !copy) return <></>;
    return (
      <RequestUpdateCard
        requestTitle={r.title}
        at={e.at}
        title={copy.title}
        body={copy.body}
        onViewStatus={() => messagingHub.showSupportView({ kind: 'request', id: r.id })}
      />
    );
  };

  if (view.kind === 'requests') {
    return (
      <>
      <RequestsList
        requests={requests}
        onBack={() => messagingHub.showSupportView({ kind: 'chat' })}
        onOpen={(id) => messagingHub.showSupportView({ kind: 'request', id, from: 'requests' })}
      />
      {native && nativeFeed.hasNextPage && !nativeFeedMissing(nativeFeed.error) && <button type="button" disabled={nativeFeed.isFetchingNextPage} onClick={() => void nativeFeed.fetchNextPage()} className="shrink-0 px-15 py-10 text-12">View all</button>}
      </>
    );
  }
  if (view.kind === 'request') {
    const request = requests.find((r) => r.id === view.id);
    if (request) {
      const backToList = native ? view.from === 'requests' : open.length > 1;
      return (
        <RequestDetail
          request={request}
          backLabel={backToList ? 'Requests' : 'Chat'}
          onBack={() => messagingHub.showSupportView(backToList ? { kind: 'requests' } : { kind: 'chat' })}
          events={nativeFeed.data?.events ?? []}
          hasMoreEvents={native && Boolean(nativeFeed.data?.has_more_events && nativeFeed.hasNextPage) && !nativeFeedMissing(nativeFeed.error)}
          loadingMoreEvents={nativeFeed.isFetchingNextPage}
          onLoadMoreEvents={() => void nativeFeed.fetchNextPage()}
        />
      );
    }
  }

  return (
    <>
      <div className="shrink-0 flex items-center gap-10 px-15 py-12 border-b border-line-subtle bg-raised">
        <SupportAvatar />
        <div className="min-w-0">
          <div className="text-13 leading-120">System Support{native && <SupportInfo />}</div>
          <div className="text-12 text-ink-muted leading-130 mt-2">
            Orders, shipping, billing and account
          </div>
        </div>
      </div>

      {native ? requests.length > 0 && (
        <button
          type="button"
          onClick={() => messagingHub.showSupportView({ kind: 'requests' })}
          className="shrink-0 w-full flex items-center justify-between gap-10 px-15 py-10 border-b border-line-subtle bg-page hover:bg-raised text-left"
        >
          <span className="font-mono uppercase text-10 text-ink-muted">
            {open.length > 0 ? `Your requests · ${open.length} open` : 'Past requests'}
          </span>
          <ArrowInline className="text-ink-muted" />
        </button>
      ) : (
        <RequestStrip
          requests={open}
          onOpen={(id) => messagingHub.showSupportView({ kind: 'request', id })}
          onViewAll={() => messagingHub.showSupportView({ kind: 'requests' })}
        />
      )}

      <MessageList
        nativeAttribution={native}
        requestTitles={native ? new Map(requests.map(request => [request.id, request.title])) : undefined}
        onOpenRequest={native ? (id) => messagingHub.showSupportView({ kind: 'request', id }) : undefined}
        messages={timeline.merged}
        ready={thread !== undefined}
        loading={sendMessage.isPending}
        renderMessage={renderMessage}
        renderEvidence={native ? renderImages : undefined}
      />

      {native && (ownImages.refused || ownImages.items.length > 0) && (
        <div className="ph-no-capture shrink-0 flex flex-col gap-6 border-t border-line-subtle px-15 py-10 text-12 text-ink-muted">
          {ownImages.refused && <p role="alert">Choose up to 8 JPEG, PNG or WebP images, each up to 8 MB.</p>}
          {ownImages.items.map(item => (
            <div key={item.key} className="flex items-center gap-10">
              <span className="min-w-0 flex-1 truncate text-ink">{item.file.name}</span>
              {!item.attachment && !item.failed && <span role="status">Uploading…</span>}
              {item.failed && <span>Upload not confirmed</span>}
              {item.failed && (
                <button type="button" onClick={() => uploadImage(draftOwner, item)} className="underline">Retry image</button>
              )}
              <button
                type="button"
                aria-label="Remove attachment"
                onClick={() => removeImage(item)}
                className="p-2"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <Composer
        placeholder="Message System Support…"
        value={draftValue}
        onValueChange={value => setDraft({ owner: draftOwner, value })}
        sending={sendMessage.isPending}
        picker={native ? {
          accept: SUPPORT_ATTACHMENT_TYPES.join(','),
          disabled: !bootstrap?.patient.id || !thread?.thread_id || ownImages.items.length >= SUPPORT_ATTACHMENT_LIMIT,
          busy: ownImages.items.some(item => !item.attachment && !item.failed),
          onPick: pickImages,
        } : undefined}
        onSend={(text) => {
          if (sendMessage.isPending) return;
          sentOwner.current = draftOwner;
          const ready = native ? ownImages.items.filter(item => item.attachment) : [];
          for (const item of ready) pendingUploads.current.delete(item.key);
          sentImages.current = ready;
          if (native) setImages({ owner: draftOwner, items: [], refused: false });
          // Every message goes through arrival; Sonny's sorting decides which
          // request it's about (WF07-R2).
          sendMessage.mutate(ready.length
            ? { body: text, attachment_ids: ready.flatMap(item => item.attachment ? [item.attachment.id] : []) }
            : text);
        }}
      />
    </>
  );
}

function AttachmentImage({ attachment, owner, alt }: { attachment: SupportAttachment; owner: string; alt: string }) {
  const read = useReadSupportAttachment();
  const resetRead = read.reset;
  const generation = useRef(0);
  const activeUrl = useRef<string | null>(null);
  const [image, setImage] = useState<{ url: string; owner: string; id: string } | null>(null);
  const url = image?.owner === owner && image.id === attachment.id ? image.url : null;
  useEffect(() => {
    setImage(null);
    resetRead();
    return () => {
      generation.current += 1;
      if (activeUrl.current) URL.revokeObjectURL(activeUrl.current);
      activeUrl.current = null;
    };
  }, [attachment.id, owner, resetRead]);

  async function open() {
    if (read.isPending || activeUrl.current) return;
    const current = generation.current;
    const blob = await read.mutateAsync(attachment.id).catch(() => null);
    if (!blob || current !== generation.current || activeUrl.current) return;
    activeUrl.current = URL.createObjectURL(blob);
    setImage({ url: activeUrl.current, owner, id: attachment.id });
  }
  return (
    <div className="text-14">
      {url ? (
        <img src={url} alt={alt} className="max-h-[320px] max-w-full object-contain" />
      ) : (
        <button type="button" disabled={read.isPending} onClick={() => void open()}>
          {read.isPending ? 'Loading photo…' : 'View attached photo'}
        </button>
      )}
      {read.isError && <p role="alert">The photo could not be loaded. Try again.</p>}
    </div>
  );
}

const SUPPORT_INFO = "A private chat with our concierge team. Sonny, our AI assistant, answers right away, day or night. When you need a person, Sonny opens a request and brings in a specialist, who replies here. You might hear from more than one specialist, depending on who's available.";

/**
 * "About System Support": opens on hover or tap, closes on mouse leave, a
 * second tap, Escape or a click outside. A mouse click keeps it open so the
 * hover that already opened it is not undone.
 */
export function SupportInfo() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const pointer = useRef('');
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const hover = (value: boolean) => (event: ReactPointerEvent) => {
    if (event.pointerType === 'mouse') setOpen(value);
  };
  return (
    <span ref={root} className="relative inline-flex align-middle ml-6" onPointerEnter={hover(true)} onPointerLeave={hover(false)}>
      <button
        type="button"
        aria-label="About System Support"
        aria-expanded={open}
        aria-controls={id}
        onPointerDown={(event) => { pointer.current = event.pointerType; }}
        onClick={() => {
          const mouse = pointer.current === 'mouse';
          pointer.current = '';
          setOpen(value => mouse || !value);
        }}
        className="flex -m-3 p-3 text-ink-muted hover:text-ink"
      >
        <Info size={14} strokeWidth={1.5} aria-hidden />
      </button>
      {open && (
        <span id={id} role="tooltip" className="absolute left-1/2 top-full z-popover mt-6 w-[24rem] max-w-[calc(100vw-3rem)] -translate-x-1/2 border border-line bg-raised px-12 py-10 text-12 leading-140 text-ink-secondary text-left shadow-lg">
          {SUPPORT_INFO}
        </span>
      )}
    </span>
  );
}
